"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const L = {
    fr: {
        title: "Trouvez l'affirmation fausse",
        intro: "Trois affirmations produites par un modèle. L'une est fausse. Laquelle ?",
        correct: "Trouvé.",
        wrong: "Raté. C'était la troisième proposition de la liste ci-dessus.",
        why: "Pourquoi c'est celle-là",
        lesson: "Rien dans la formulation ne la distinguait des deux autres : même assurance, même ton, même longueur. C'est exactement ce qui rend une hallucination dangereuse dans un texte par ailleurs juste.",
        again: "Un autre exemple",
        score: "Trouvé {n} fois sur {total}",
    },
    en: {
        title: "Find the false statement",
        intro: "Three statements produced by a model. One is false. Which one?",
        correct: "Found it.",
        wrong: "Missed. It was the third option in the list above.",
        why: "Why that one",
        lesson: "Nothing in the wording set it apart from the other two: same confidence, same tone, same length. That is exactly what makes a hallucination dangerous inside an otherwise accurate text.",
        again: "Another example",
        score: "Found {n} out of {total}",
    },
};

const ROUNDS = {
    fr: [
        {
            statements: [
                "Un modèle de langage produit sa réponse mot après mot, en choisissant à chaque fois la suite la plus probable.",
                "La fenêtre de contexte comprend votre consigne, l'historique de la conversation et la réponse en cours de rédaction.",
                "Chaque réponse est accompagnée d'un indice de confiance interne, ce qui permet de repérer automatiquement les passages douteux.",
            ],
            falseIndex: 2,
            why: "Un modèle ne dispose d'aucun indicateur de confiance exploitable. C'est précisément pour cela qu'il ne sait pas dire qu'il ne sait pas, et que lui demander s'il est sûr ne sert à rien.",
        },
        {
            statements: [
                "Un webhook est envoyé par le service au moment où l'événement se produit, sans que vous ayez à demander.",
                "Les API limitent presque toujours le nombre d'appels acceptés par minute.",
                "Un webhook ne peut jamais livrer deux fois le même événement, chaque message étant unique.",
            ],
            falseIndex: 2,
            why: "Les services rejouent les webhooks après un échec, ce qui produit des doublons. C'est pour cette raison qu'une automatisation doit reconnaître un événement déjà traité avant d'agir.",
        },
        {
            statements: [
                "Les jetons envoyés au modèle coûtent moins cher que les jetons qu'il produit.",
                "Dans une conversation, tout l'historique est renvoyé au modèle à chaque nouveau message.",
                "Le cache de prompt s'applique à n'importe quelle portion répétée de la consigne, où qu'elle se trouve dans le texte.",
            ],
            falseIndex: 2,
            why: "Le cache porte sur le début de la consigne. Une seule variable placée en tête suffit à l'invalider entièrement, ce qui explique que beaucoup de mises en cache ne servent à rien sans qu'on s'en aperçoive.",
        },
    ],
    en: [
        {
            statements: [
                "A language model produces its answer word by word, each time choosing the most likely continuation.",
                "The context window covers your instruction, the conversation history and the answer being written.",
                "Every answer carries an internal confidence score, which makes it possible to flag doubtful passages automatically.",
            ],
            falseIndex: 2,
            why: "A model has no usable confidence indicator. That is precisely why it cannot tell you that it does not know, and why asking whether it is sure achieves nothing.",
        },
        {
            statements: [
                "A webhook is sent by the service the moment the event happens, without you having to ask.",
                "APIs almost always limit the number of calls accepted per minute.",
                "A webhook can never deliver the same event twice, since each message is unique.",
            ],
            falseIndex: 2,
            why: "Services replay webhooks after a failure, which produces duplicates. This is why an automation must recognise an already-handled event before acting.",
        },
        {
            statements: [
                "Tokens sent to the model cost less than the tokens it produces.",
                "In a conversation, the whole history is resent to the model with every new message.",
                "Prompt caching applies to any repeated portion of the instruction, wherever it sits in the text.",
            ],
            falseIndex: 2,
            why: "Caching covers the beginning of the instruction. A single variable placed at the head invalidates it entirely, which is why many caching setups achieve nothing without anyone noticing.",
        },
    ],
};

export default function SpotTheHallucination({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const rounds = ROUNDS[lang] || ROUNDS.fr;

    const [round, setRound] = useState(0);
    const [picked, setPicked] = useState(null);
    const [score, setScore] = useState({ found: 0, played: 0 });

    // Les affirmations sont présentées dans un ordre stable : la fausse n'est
    // pas toujours la dernière d'un tour à l'autre, l'ordre change avec le tour.
    const current = rounds[round % rounds.length];
    const order = [(round + 1) % 3, (round + 2) % 3, round % 3];
    const shown = order.map((i) => ({
        text: current.statements[i],
        isFalse: i === current.falseIndex,
    }));

    const answered = picked !== null;
    const gotIt = answered && shown[picked].isFalse;

    const choose = (index) => {
        if (answered) return;
        setPicked(index);
        setScore((s) => ({
            found: s.found + (shown[index].isFalse ? 1 : 0),
            played: s.played + 1,
        }));
    };

    const next = () => {
        setPicked(null);
        setRound((r) => r + 1);
    };

    return (
        <div className="my-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6 text-base leading-normal">
            <div className="flex items-center gap-2 mb-2">
                <SolarIcon name="minimalistic-magnifer-linear" className="w-5 h-5 text-primary" />
                <div className="font-bold dark:text-white text-base leading-snug">{t.title}</div>
            </div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mb-4">{t.intro}</p>

            <div className="space-y-2">
                {shown.map((s, i) => {
                    const isPicked = picked === i;
                    let tone =
                        "border-gray-200 dark:border-white/10 hover:border-primary/50";

                    if (answered) {
                        if (s.isFalse) {
                            tone = "border-red-500/60 bg-red-500/[0.07]";
                        } else if (isPicked) {
                            tone = "border-amber-500/60 bg-amber-500/[0.07]";
                        } else {
                            tone = "border-gray-200 dark:border-white/10 opacity-60";
                        }
                    }

                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => choose(i)}
                            disabled={answered}
                            className={`w-full text-left p-3 rounded-xl border text-sm transition-colors dark:text-white ${tone} ${
                                answered ? "cursor-default" : "cursor-pointer"
                            }`}
                        >
                            <span className="flex gap-2.5">
                                <span className="font-bold text-primary shrink-0">
                                    {String.fromCharCode(65 + i)}
                                </span>
                                <span>{s.text}</span>
                            </span>
                        </button>
                    );
                })}
            </div>

            {answered && (
                <div className="mt-4">
                    <div
                        className={`text-sm font-bold mb-2 ${
                            gotIt
                                ? "text-green-700 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                        }`}
                    >
                        {gotIt ? t.correct : t.wrong}
                    </div>

                    <div className="rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-4">
                        <div className="text-xs font-bold uppercase tracking-wide text-primary mb-1">
                            {t.why}
                        </div>
                        <p className="text-sm text-gray-700 dark:text-lightBlue mb-3">
                            {current.why}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                            {t.lesson}
                        </p>
                    </div>

                    <div className="flex items-center justify-between gap-3 mt-4">
                        <span className="text-xs text-gray-500 dark:text-lightBlue/70">
                            {t.score
                                .replace("{n}", String(score.found))
                                .replace("{total}", String(score.played))}
                        </span>
                        <button
                            type="button"
                            onClick={next}
                            className="text-sm font-semibold text-primary hover:text-primary-100"
                        >
                            {t.again}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
